import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  TextField,
  Alert,
  Stack,
  MenuItem,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import {
  useGetMovieReportsQuery,
  useLazyGetMovieReportsQuery,
} from "../../../services/movie-report.query";
import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../../components/common/StatusChip";
import { RowActions } from "../../../components/common/RowActions";
import { useTableState } from "../../../hooks/useTableState";
import { MRT_SortingState } from "material-react-table";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { Notify } from "../../../layouts/notify";
import {
  fetchAllInBatches,
  mapRowsInChunks,
  yieldToMainThread,
} from "../../../utils/exportUtils";

export default function DeputyMovieReportPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date();
  const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
  const defaultStartDate = firstDayOfYear.toISOString().split("T")[0];
  const defaultEndDate = today.toISOString().split("T")[0];

  const defaultFilters = useMemo(
    () => ({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      statusFilter: "PENDING",
      violationFilter: "ALL",
      deputySearch: "",
      theatreSearch: "",
    }),
    [defaultStartDate, defaultEndDate]
  );

  // Persistent URL Table State: saves and restores page, filters, and sort order across route transitions
  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
    defaultSortBy: "created_at",
    defaultSortOrder: "desc",
  });

  // Local draft inputs before clicking Search
  const [draftStartDate, setDraftStartDate] = useState(filters.startDate);
  const [draftEndDate, setDraftEndDate] = useState(filters.endDate);
  const [draftStatus, setDraftStatus] = useState(filters.statusFilter);
  const [draftViolation, setDraftViolation] = useState(filters.violationFilter);
  const [draftDeputySearch, setDraftDeputySearch] = useState(filters.deputySearch);
  const [draftTheatreSearch, setDraftTheatreSearch] = useState(filters.theatreSearch);

  // Sync draft inputs when URL filters change (e.g. Back button or external navigation)
  useEffect(() => {
    setDraftStartDate(filters.startDate);
    setDraftEndDate(filters.endDate);
    setDraftStatus(filters.statusFilter);
    setDraftViolation(filters.violationFilter);
    setDraftDeputySearch(filters.deputySearch);
    setDraftTheatreSearch(filters.theatreSearch);
  }, [filters]);

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  const {
    data: movieReportData,
    isLoading,
    isFetching,
    isError,
  } = useGetMovieReportsQuery(
    {
      startDate: filters.startDate,
      endDate: filters.endDate,
      start,
      end,
      statusFilter: filters.statusFilter,
      violationFilter: filters.violationFilter,
      deputySearch: filters.deputySearch,
      theatreSearch: filters.theatreSearch,
      sortColumn: sortBy,
      sortDirection: sortOrder,
      subRole: "DEPUTY",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const totalRecords = movieReportData?.count || 0;
  const dataChunk = movieReportData?.list || [];

  const handleSearch = () => {
    setFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
      statusFilter: draftStatus,
      violationFilter: draftViolation,
      deputySearch: draftDeputySearch.trim(),
      theatreSearch: draftTheatreSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const [triggerFetchAllMovieReports] = useLazyGetMovieReportsQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportProgress, setExportProgress] = useState<number | null>(null);

  const viewReview = (row: { original: any }) => {
    const currentUrl = `${location.pathname}${location.search}`;
    sessionStorage.setItem("last_deputy_movies_url", currentUrl);
    navigate(`/deputies/details?id=${row.original.id}`, {
      state: { deputyReview: row.original, returnUrl: currentUrl },
    });
  };

  const fetchAllDeputyMovieReports = async () => {
    return fetchAllInBatches<any>({
      batchSize: 1000,
      knownTotal: Number(totalRecords || 0),
      fetchBatch: async (offset, limit) => {
        const res = await triggerFetchAllMovieReports(
          {
            startDate: filters.startDate,
            endDate: filters.endDate,
            start: offset,
            end: offset + limit - 1,
            statusFilter: filters.statusFilter,
            violationFilter: filters.violationFilter,
            deputySearch: filters.deputySearch,
            theatreSearch: filters.theatreSearch,
            sortColumn: sortBy,
            sortDirection: sortOrder,
            subRole: "DEPUTY",
          },
          false
        ).unwrap();
        return {
          rows: res?.list || [],
          total: res?.count ?? Number(totalRecords || 0),
        };
      },
      onProgress: (fetched, total, percent) => {
        setExportProgress(percent);
        if (total && total > 0) {
          setExportMessage(
            `Fetching movie reports: ${fetched.toLocaleString()} of ${total.toLocaleString()} records...`
          );
        } else {
          setExportMessage(
            `Fetching movie reports: ${fetched.toLocaleString()} records loaded...`
          );
        }
      },
    });
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching deputy movie reports...");
    try {
      const allRows = await fetchAllDeputyMovieReports();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(
        `Formatting ${allRows.length.toLocaleString()} deputy movie reports for PDF...`
      );
      await yieldToMainThread(30);

      const tableRows = await mapRowsInChunks(
        allRows,
        (item: any, idx: number) => [
          idx + 1,
          String(item.id || "—"),
          `${item.deputy?.last_name ? `${item.deputy.last_name}, ` : ""}${item.deputy?.name || "Deputy"}`,
          String(item.deputy?.deputy_control_number || "—"),
          String(item.ticket_request?.cinema_name || "—"),
          String(item.ticket_request?.movie_title || "—"),
          item.created_at ? format(new Date(item.created_at), "MMM dd, yyyy · hh:mm a") : "—",
          item.has_violation ? "With Violation" : "None",
          String(item.status || "PENDING"),
        ],
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing PDF rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(
        `Rendering official PDF document (${allRows.length.toLocaleString()} reports)...`
      );
      await yieldToMainThread(40);

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text("Deputy Movie Monitoring Directory", 14, 21);

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      const dateRangeText = `Date Range: ${filters.startDate || "Start"} to ${filters.endDate || "End"}`;
      doc.text(
        `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  Status: ${filters.statusFilter}  |  ${dateRangeText}  |  Total Records: ${allRows.length.toLocaleString()}`,
        14,
        27
      );

      const tableColumn = [
        "#",
        "ID",
        "Deputy Name",
        "Control No.",
        "Theatre House",
        "Movie Title",
        "Submitted At",
        "Violation",
        "Status",
      ];

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 14 },
      });

      const totalPages = (doc as any).internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - 14,
          pageHeight - 6,
          { align: "right" }
        );
      }

      setExportProgress(100);
      setExportMessage("Download starting...");
      await yieldToMainThread(20);

      doc.save(`MTRCB_Deputy_Movie_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputy movie reports to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching deputy movie reports...");
    try {
      const allRows = await fetchAllDeputyMovieReports();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(
        `Formatting ${allRows.length.toLocaleString()} deputy movie reports for Excel...`
      );
      await yieldToMainThread(30);

      const rows = await mapRowsInChunks(
        allRows,
        (item: any, idx: number) => ({
          "No.": idx + 1,
          "Report ID": item.id ?? "—",
          "Deputy Name": `${item.deputy?.last_name ? `${item.deputy.last_name}, ` : ""}${item.deputy?.name || "Deputy"}`,
          "Control Number": item.deputy?.deputy_control_number || "—",
          "Theatre House": item.ticket_request?.cinema_name || "—",
          "Movie Title": item.ticket_request?.movie_title || "—",
          "Submitted At": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd hh:mm a") : "—",
          "Violation": item.has_violation ? "With Violation" : "None",
          "Status": item.status || "PENDING",
        }),
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing Excel rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(
        `Writing Excel workbook (${allRows.length.toLocaleString()} reports)...`
      );
      await yieldToMainThread(40);

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Deputy Movie Reports");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 12 },
        { wch: 28 },
        { wch: 20 },
        { wch: 30 },
        { wch: 30 },
        { wch: 22 },
        { wch: 16 },
        { wch: 16 },
      ];

      XLSX.writeFile(wb, `MTRCB_Deputy_Movie_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      setExportProgress(100);
      setExportMessage("Download complete!");
      await yieldToMainThread(20);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputy movie reports to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 65,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            {(page - 1) * pageSize + row.index + 1}
          </Typography>
        ),
      },
      {
        accessorKey: "deputy.name",
        header: "Deputy",
        enableSorting: false,
        size: 260,
        Cell: ({ row }: any) => {
          const deputy = row.original.deputy;
          const image =
            deputy?.profile_picture ||
            "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png";

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar
                src={image}
                alt={deputy?.name || "Deputy"}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid #E2E8F0",
                }}
              />
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                  {deputy?.last_name ? `${deputy.last_name}, ` : ""}
                  {deputy?.name || "Deputy"}
                </Typography>
                {deputy?.deputy_control_number && (
                  <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
                    {deputy.deputy_control_number}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        accessorKey: "ticket_request.cinema_name",
        header: "Theatre House",
        enableSorting: false,
        size: 280,
        Cell: ({ row }: any) => (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {row.original.ticket_request?.cinema_name || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Submitted At",
        size: 200,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {cell.getValue()
              ? format(new Date(cell.getValue()), "MMM dd, yyyy · hh:mm a")
              : "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "has_violation",
        header: "Violation",
        size: 140,
        Cell: ({ cell }: any) => {
          const hasViolation = Boolean(cell.getValue());
          return (
            <StatusChip
              label={hasViolation ? "With Violation" : "None"}
              tone={hasViolation ? "error" : "neutral"}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ row }: any) => {
          const status = row.original.status || "PENDING";
          return <StatusChip label={status} />;
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "View",
                onClick: () => viewReview(row),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize, viewReview]
  );

  return (
    <DefaultPage
      title="Deputy Movie Reports"
      subTitle="Manage and review movie monitoring reports submitted by deputies."
    >
      {/* Standardized Gold-Standard Filter Panel */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              label="End Date"
              type="date"
              size="small"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="ACKNOWLEDGED">ACKNOWLEDGED</MenuItem>
              <MenuItem value="DUPLICATE">DUPLICATE</MenuItem>
              <MenuItem value="REJECTED">REJECTED</MenuItem>
              <MenuItem value="ALL">ALL STATUSES</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Violation"
              value={draftViolation}
              onChange={(e) => setDraftViolation(e.target.value)}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="ALL">ALL VIOLATIONS</MenuItem>
              <MenuItem value="WITH">With Violation</MenuItem>
              <MenuItem value="NONE">No Violation</MenuItem>
            </TextField>

            <TextField
              label="Deputy Name"
              size="small"
              placeholder="Search deputy..."
              value={draftDeputySearch}
              onChange={(e) => setDraftDeputySearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 190,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              label="Theatre House"
              size="small"
              placeholder="Search theatre..."
              value={draftTheatreSearch}
              onChange={(e) => setDraftTheatreSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 190,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting || totalRecords === 0}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting || totalRecords === 0}
                sx={{
                  color: "#0072BC",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#F0F9FF",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Feedback */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": {
              color: "#00934C",
            },
          }}
        >
          Total matched records: <strong>{Number(totalRecords || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      {/* Upgraded DataTableFive with skeleton state & persistent page/sort */}
      <DataTableFive
        data={dataChunk}
        total={totalRecords}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={viewReview}
        sorting={sorting}
        onSortingChange={(newSorting) => {
          if (newSorting.length > 0) {
            updateTableSorting(newSorting[0].id, newSorting[0].desc ? "desc" : "asc");
          }
        }}
      />

      <ExportLoadingBackdrop
        open={isExporting}
        message={exportMessage}
        progress={exportProgress}
      />
    </DefaultPage>
  );
}